import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Home as HomeIcon,
  FileText,
  Stamp,
  MapPin,
  Info,
  HelpCircle,
  Building2,
  Phone,
  Mail,
  Menu,
  ArrowRight,
  LogOut,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const LOGO = "/arps-logo-hd.png";

const NAV_ITEMS = [
  { to: "/", label: "Home", icon: HomeIcon, testid: "nav-item-home", end: true },
  { to: "/about", label: "About", icon: Info, testid: "nav-item-about" },
  { to: "/process-serving", label: "Process Serving", icon: FileText, testid: "nav-item-process-serving" },
  { to: "/notary-services", label: "Notary Services", icon: Stamp, testid: "nav-item-notary-services" },
  { to: "/service-areas", label: "Service Areas", icon: MapPin, testid: "nav-item-service-areas" },
  { to: "/faq", label: "FAQ", icon: HelpCircle, testid: "nav-item-faq" },
  { to: "/agency-access", label: "For Agencies", icon: Building2, testid: "nav-item-agency-access" },
  { to: "/contact", label: "Contact Us", icon: Phone, testid: "nav-item-contact" },
];

export function StartServiceButton({ testid = "start-service-cta", className = "", label = "Start a Service", to: toOverride }) {
  const { user } = useAuth();
  // Public client-acquisition CTA: it always funnels to CLIENT REGISTRATION for public/non-client
  // visitors (logged-out, admin, server). Only an authenticated CLIENT is fast-tracked into the
  // ordering flow. It never routes to the Admin/Server dashboards.
  // `toOverride` lets a specific page (e.g. Notary Services) send the CTA to a public intake instead.
  const to = toOverride
    ? toOverride
    : user && user.role === "client"
    ? "/client/start"
    : "/login?mode=register";
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-sky-600 px-6 text-base font-semibold text-white shadow-lg shadow-sky-900/20 transition hover:bg-sky-700 active:scale-[0.98] ${className}`}
    >
      {label} <ArrowRight className="h-5 w-5" />
    </Link>
  );
}

function NavList({ onNavigate }) {
  return (
    <nav data-testid="nav-sidebar" className="flex flex-col gap-1.5">
      {NAV_ITEMS.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          data-testid={it.testid}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex min-h-[48px] items-center gap-3 rounded-xl px-4 text-[15px] font-medium transition ${
              isActive ? "bg-sky-600 text-white" : "text-slate-200 hover:bg-slate-800 hover:text-white"
            }`
          }
        >
          <it.icon className="h-5 w-5 shrink-0" />
          {it.label}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarInner({ onNavigate }) {
  const { user, logout } = useAuth();
  return (
    <div className="flex h-full flex-col">
      <Link
        to="/"
        onClick={onNavigate}
        aria-label="AR Process Service and Notary home"
        className="flex items-center justify-center border-b border-slate-800 px-4 py-6"
      >
        <div className="rounded-2xl bg-white p-3 shadow-sm">
          <img data-testid="brand-logo" src={LOGO} alt="AR Process Service and Notary" className="h-20 w-auto" />
        </div>
      </Link>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6">
        <NavList onNavigate={onNavigate} />
        <div className="mt-6">
          <StartServiceButton testid="start-service-cta-sidebar" className="w-full" />
        </div>
        {user && (
          <button
            data-testid="marketing-logout-button"
            onClick={() => { logout(); onNavigate && onNavigate(); }}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-700 px-4 py-2.5 text-sm font-medium text-slate-300 transition hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-400"
          >
            <LogOut className="h-4 w-4" /> Log out ({user.email})
          </button>
        )}
      </div>
      <div className="border-t border-slate-800 px-5 py-5 text-sm text-slate-300">
        <a href="tel:+19016952500" className="flex min-h-[44px] items-center gap-2 font-semibold text-white">
          <Phone className="h-4 w-4 shrink-0 text-sky-400" /> (901) 695-2500
        </a>
        <a href="mailto:admin@artadmin.net" className="mt-1 flex min-h-[44px] items-center gap-2 text-slate-300">
          <Mail className="h-4 w-4 shrink-0 text-sky-400" /> admin@artadmin.net
        </a>
        <p className="mt-2 flex items-start gap-2 text-slate-400">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-sky-400" /> 4730 Riverdale Rd, Memphis, TN 38141
        </p>
      </div>
    </div>
  );
}

function MarketingFooter() {
  return (
    <footer className="border-t border-slate-200 bg-slate-900 px-5 py-12 text-slate-300 sm:px-8">
      <div className="mx-auto grid max-w-5xl gap-10 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="inline-flex rounded-xl bg-white p-2">
            <img src={LOGO} alt="AR Process Service and Notary" className="h-14 w-auto" />
          </div>
          <p className="mt-4 text-sm italic text-slate-400">
            Every Service. Every Attempt. Every Detail. Documented.
          </p>
        </div>
        <div>
          <p className="text-sm font-bold uppercase tracking-widest text-sky-400">Services</p>
          <ul className="mt-3 space-y-2 text-[15px]">
            <li><Link to="/process-serving" className="hover:text-white">Process Serving</Link></li>
            <li><Link to="/notary-services" className="hover:text-white">Notary Services</Link></li>
            <li><Link to="/service-areas" className="hover:text-white">Service Areas</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-sm font-bold uppercase tracking-widest text-sky-400">Company</p>
          <ul className="mt-3 space-y-2 text-[15px]">
            <li><Link to="/about" className="hover:text-white">About</Link></li>
            <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
            <li><Link to="/login" className="hover:text-white">Client Sign In</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-sm font-bold uppercase tracking-widest text-sky-400">Contact</p>
          <ul className="mt-3 space-y-2 text-[15px]">
            <li><a href="tel:+19016952500" className="hover:text-white">(901) 695-2500</a></li>
            <li><a href="mailto:admin@artadmin.net" className="hover:text-white">admin@artadmin.net</a></li>
            <li className="text-slate-400">4730 Riverdale Rd,<br />Memphis, TN 38141</li>
          </ul>
        </div>
      </div>
      <p className="mx-auto mt-10 max-w-5xl border-t border-slate-800 pt-6 text-sm text-slate-500">
        © AR Process Service and Notary. Serving the Memphis Metro and Shelby County.
      </p>
    </footer>
  );
}

export function MarketingLayout({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-72 flex-col bg-slate-900 lg:flex">
        <SidebarInner />
      </aside>

      <header className="sticky top-0 z-40 flex items-center justify-between gap-2 border-b border-slate-800 bg-slate-900 px-4 py-2.5 lg:hidden">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-slate-700 text-white"
            >
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 border-0 bg-slate-900 p-0">
            <SidebarInner onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
        <Link to="/" aria-label="Home" className="rounded-lg bg-white p-1.5">
          <img src={LOGO} alt="AR Process Service and Notary" className="h-10 w-auto" />
        </Link>
        <StartServiceButton testid="start-service-cta-mobilebar" label="Start a Service" className="!min-h-[44px] !px-4 !text-sm" />
      </header>

      <main className="lg:pl-72">
        {children}
        <MarketingFooter />
      </main>
    </div>
  );
}
